/**
 * Os documentos do paciente na tela (#811, fase 2): os textos e a conferência
 * antes de enviar. Sem React, para o teste não precisar de tela.
 */

import {
  LIMITES_DO_DOCUMENTO_DO_PACIENTE,
  type CategoriaDoDocumentoDoPaciente,
} from '@cliqcrm/contracts';

export const NOMES_DA_CATEGORIA: Readonly<Record<CategoriaDoDocumentoDoPaciente, string>> = {
  identidade: 'Identidade',
  carteirinha: 'Carteirinha',
  termo: 'Termo',
  contrato: 'Contrato',
  outro: 'Outro',
};

/** `1536` → `1,5 KB`. */
export function tamanhoLegivel(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} KB`;
  return `${(kb / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

export type EstadoDaValidade = 'vencido' | 'vence-em-breve' | 'valido';

/**
 * Vencido, vencendo nos próximos 30 dias, ou válido; `null` sem validade.
 * As datas são de calendário (`AAAA-MM-DD`), comparadas como texto.
 */
export function estadoDaValidade(validade: string | null, hoje: string): EstadoDaValidade | null {
  if (validade === null) return null;
  if (validade < hoje) return 'vencido';
  const [ano, mes, dia] = hoje.split('-').map(Number) as [number, number, number];
  const limite = new Date(Date.UTC(ano, mes - 1, dia + 30)).toISOString().slice(0, 10);
  return validade <= limite ? 'vence-em-breve' : 'valido';
}

/** `2027-03-31` → `31/03/2027`. */
export function dataPorExtenso(data: string): string {
  const [ano, mes, dia] = data.split('-');
  return `${dia}/${mes}/${ano}`;
}

/**
 * Separa os arquivos que cabem dos que passam do limite, antes de enviar: o
 * servidor recusaria o envio inteiro por um arquivo grande demais.
 */
export function separarPorTamanho<T extends { readonly size: number; readonly name: string }>(
  arquivos: readonly T[],
): { aceitos: T[]; recusados: string[] } {
  const limite = LIMITES_DO_DOCUMENTO_DO_PACIENTE.megabytesPorArquivo * 1024 * 1024;
  const aceitos: T[] = [];
  const recusados: string[] = [];
  for (const arquivo of arquivos) {
    if (arquivo.size === 0 || arquivo.size > limite) recusados.push(arquivo.name);
    else aceitos.push(arquivo);
  }
  return {
    aceitos: aceitos.slice(0, LIMITES_DO_DOCUMENTO_DO_PACIENTE.arquivosPorEnvio),
    recusados: [
      ...recusados,
      ...aceitos.slice(LIMITES_DO_DOCUMENTO_DO_PACIENTE.arquivosPorEnvio).map((a) => a.name),
    ],
  };
}
